import React from "react";
import { Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Motion";
import { BrandMarquee } from "@/components/BrandMarquee";
import { CtaBand } from "@/pages/Home";

const Services = () => {
  const { t } = useLang();
  return (
    <div data-testid="services-page">
      <PageHeader eyebrow={t.servicesPage.eyebrow} title={t.servicesPage.title} lead={t.servicesPage.lead} />

      <section className="bg-ivory">
        {t.services.map((s, i) => (
          <div key={i} className={`border-b border-navy/10 ${i % 2 === 1 ? "bg-white" : ""}`} data-testid={`service-${i}`}>
            <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-4">
                <Reveal>
                  <span className="font-serif text-7xl lg:text-9xl text-gold/50 leading-none">{s.num}</span>
                </Reveal>
                <Reveal delay={0.1}>
                  <h2 className="mt-6 font-serif text-4xl lg:text-5xl text-navy">{s.title}</h2>
                </Reveal>
                <Reveal delay={0.15}>
                  <p className="mt-4 text-gold text-sm uppercase tracking-[0.12em] font-semibold">{s.short}</p>
                </Reveal>
              </div>
              <div className="lg:col-span-8 lg:pl-16 lg:border-l border-navy/10">
                <Reveal delay={0.1}>
                  <p className="text-navy/75 text-lg lg:text-xl leading-relaxed max-w-2xl">{s.desc}</p>
                </Reveal>
                <div className="mt-12 grid sm:grid-cols-2 gap-x-10 gap-y-5">
                  {s.points.map((p, j) => (
                    <Reveal key={j} delay={0.05 * j}>
                      <div className="flex items-center gap-4 border-b border-navy/10 pb-4">
                        <span className="flex items-center justify-center w-7 h-7 border border-gold text-gold shrink-0">
                          <Check size={14} strokeWidth={2} />
                        </span>
                        <span className="text-navy/80">{p}</span>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      <BrandMarquee items={t.marquee} dark />

      {/* APPROACH */}
      <section className="py-24 lg:py-32 bg-navy text-ivory relative overflow-hidden grain" data-testid="approach-section">
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12">
          <Reveal><span className="text-[12px] uppercase tracking-[0.3em] text-gold font-semibold">{t.servicesPage.approachTitle}</span></Reveal>
          <Reveal delay={0.1}><h2 className="mt-6 font-serif text-4xl lg:text-6xl leading-[1.05] max-w-2xl">{t.servicesPage.approachLead}</h2></Reveal>

          <div className="mt-12 flex flex-wrap gap-x-4 gap-y-3">
            {t.servicesPage.verbs.map((v, i) => (
              <Reveal key={i} delay={0.05 * i}>
                <span className="inline-block border border-gold/40 px-6 py-3 text-sm uppercase tracking-[0.14em] text-ivory/90">{v}</span>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gold/20 border border-gold/20">
            {t.servicesPage.steps.map((s, i) => (
              <Reveal key={i} delay={0.08 * i}>
                <div className="bg-navy p-8 h-full">
                  <span className="font-serif text-5xl text-gold/60">0{i + 1}</span>
                  <h3 className="mt-6 font-serif text-2xl">{s}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
};

export default Services;
